import { FileObject, FilePurpose, FileType } from '@citruscamps/citrus-client'
import React, { useState } from 'react'
import { useUploadFiles } from '../../hooks/useUploadFiles'
import { CropModal } from '../CropModel/CropModal'
import { Dropzone } from '../Dropzone/Dropzone'
import { TruncatedText } from '../TruncatedText/TruncatedText'
import { MediaActionButton } from './components/MediaActionButton'
import { MediaSelectModal } from './components/MediaSelectModal'
import { useFetchInfiniteFiles } from './hooks/useFetchFiles'

interface FileObjectFromUrlOptions {
  url?: string
  purpose: FilePurpose
  context: string
}

const getFileObjectFromUrl = ({
  url,
  purpose,
  context,
}: FileObjectFromUrlOptions): FileObject | undefined => {
  if (!url) return
  const [id, ext] = url.split('/').pop()?.split('.') || []
  const dataUrlExt = (url.startsWith('data:') && url.split(';')[0].split('/')[1]) || undefined
  const filetype: FileType | undefined =
    Object.values(FileType).find((type) => type.includes(ext)) ||
    Object.values(FileType).find((type) => dataUrlExt && type.includes(dataUrlExt))
  const filename = !url.startsWith('data:')
    ? url.split('/').pop()
    : `cropped-file.${filetype?.split('/').pop()}}`
  if (!filetype || !filename) return
  return {
    id: id || '',
    object: 'file',
    purpose: purpose || 'image',
    filename,
    filetype,
    url,
    context,
    size: 0,
    created_by: '',
  }
}

interface IProps {
  value?: string | (Pick<FileObject, 'url'> & Partial<FileObject>)
  message?: string
  disabled?: boolean
  programId: string
  purpose: FilePurpose
  accept: FileType[]
  onChange?: (file?: Pick<FileObject, 'id' | 'url'> & Partial<FileObject>) => void | Promise<void>
  onRemove?: () => void | Promise<void>
  onReset?: () => void | Promise<void>
}

export const MediaSelect = ({
  value: idOrValue,
  message = 'Drag your picture here, or click to upload it',
  programId,
  accept,
  purpose,
  onChange: handleChange,
  onRemove: handleRemove,
  onReset: handleReset,
}: IProps) => {
  const [valueId] =
    typeof idOrValue === 'string' ? [idOrValue] : idOrValue?.url?.split('/').pop()?.split('.') || []
  const { data: files = [], isLoading } = useFetchInfiniteFiles({
    programId,
    fetchProps: {
      filter: {
        id: valueId ? [valueId] : undefined,
      },
    },
    enabled: !!valueId,
    fetchAll: true,
  })
  const [
    value = getFileObjectFromUrl({
      url: typeof idOrValue !== 'string' ? idOrValue?.url : undefined,
      purpose,
      context: programId,
    }),
  ]: (FileObject | undefined)[] = files
  const { handleUpload } = useUploadFiles({ context: programId })
  const [showMediaModal, setShowMediaModal] = useState<boolean>(false)
  const [isCropModalShown, setIsCropModalShown] = useState<boolean>(false)
  const [tab, setTab] = useState<'library' | 'stock' | 'upload'>('library')

  const handleCrop = async (file: File): Promise<void> => {
    const updatedPicture = await handleUpload(file, { purpose })
    setIsCropModalShown(false)
    handleChange?.(updatedPicture)
  }

  if (isLoading && !value?.url) {
    return (
      <div className="rounded border text-center">
        <div className="d-flex w-100 p-3 animate" />
      </div>
    )
  }

  const url = typeof idOrValue === 'string' ? value?.url : value?.url || idOrValue?.url
  if (!value) {
    return (
      <div>
        <Dropzone icon="image" disabled={true} accept={accept || 'image/*'} context={programId}>
          <div className="text-center">
            <MediaActionButton
              className="btn border"
              placement="bottom"
              onOpenModal={(val = 'library') => {
                setTab(val)
                setShowMediaModal(true)
              }}
              onEdit={() => {}}
              onRemove={() => handleChange?.(undefined)}
            >
              Add image
            </MediaActionButton>
          </div>
        </Dropzone>
        <MediaSelectModal
          show={showMediaModal}
          tab={tab}
          programId={programId}
          purpose={purpose}
          accept={accept}
          dropzoneMessage={message}
          onClose={() => setShowMediaModal(false)}
          onSubmit={(file, openCrop) => {
            setShowMediaModal(false)
            if (openCrop) {
              setIsCropModalShown(true)
            }
            if (file && 'id' in file && file.id && 'url' in file && !!file.url) {
              handleChange?.({
                id: file.id,
                url: file.url,
                filename: file.filename,
                filetype: file.filetype,
              })
            }
          }}
        />
      </div>
    )
  }
  return (
    <div>
      <div className="bg-body-tertiary rounded border text-center">
        <div className="d-flex w-100 p-3">
          <div className="row-img-square rounded">
            <img src={url} alt={value?.title || value?.filename} />
          </div>
          <div className="ms-3 py-1 text-start">
            <TruncatedText className="fw-bold">{value.filename}</TruncatedText>
            <TruncatedText className="text-body-secondary">{value.filetype}</TruncatedText>
          </div>
          <div className="ms-auto py-2">
            <MediaActionButton
              value={value}
              placement="bottom-end"
              onOpenModal={(val = 'library') => {
                setTab(val)
                setShowMediaModal(true)
              }}
              onEdit={() => {
                setShowMediaModal(false)
                setIsCropModalShown(true)
              }}
              onRemove={() => {
                if (handleRemove) {
                  handleRemove()
                } else {
                  handleChange?.(undefined)
                }
              }}
            />
          </div>
        </div>
      </div>
      <MediaSelectModal
        show={showMediaModal}
        tab={tab}
        programId={programId}
        purpose={purpose}
        accept={accept}
        dropzoneMessage={message}
        onClose={() => setShowMediaModal(false)}
        onSubmit={(file, openCrop) => {
          setShowMediaModal(false)
          if (openCrop) {
            setIsCropModalShown(true)
          }
          if (file && 'id' in file && file.id && 'url' in file && !!file.url) {
            handleChange?.({
              id: file.id,
              url: file.url,
              filename: file.filename,
              filetype: file.filetype,
            })
          }
        }}
      />
      {isCropModalShown && (
        <CropModal
          file={value}
          baseHeight={1080}
          aspect={2 / 1}
          hidden={!isCropModalShown}
          onCancel={() => setIsCropModalShown(false)}
          onChange={async (url: string) => {
            if (value) {
              handleChange?.({
                ...value,
                url,
              })
            }
          }}
          onReset={handleReset}
          onSubmit={async (value) => {
            await handleCrop(value)
            setIsCropModalShown(false)
          }}
        />
      )}
    </div>
  )
}
